/** Zapytania modułu `deadlines` (TanStack Query v5). */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Query } from '@/lib/api'
import { api } from '@/lib/api'
import type { Paginated } from '@/lib/types'
import type {
  CaseOption,
  Deadline,
  DeadlineCreatePayload,
  DeadlineStats,
  DeadlineUpdatePayload,
  LawyerOption,
} from './types'

export const deadlinesKeys = {
  all: ['deadlines'] as const,
  list: (params: Query) => ['deadlines', 'list', params] as const,
  upcoming: ['deadlines', 'upcoming'] as const,
  stats: ['deadlines', 'stats'] as const,
  lawyers: ['deadlines', 'lawyers'] as const,
  cases: ['deadlines', 'cases'] as const,
}

export interface DeadlineFilters {
  page?: number
  size?: number
  case_id?: number
  owner_id?: number | ''
  status?: string
  source?: string
  from?: string
  to?: string
  open_only?: boolean
}

/** Pełna lista terminów (ekran „Terminy procesowe" — grupowanie robimy w UI). */
export function useDeadlines(filters: DeadlineFilters = {}) {
  const query: Query = { size: 200, ...filters }
  return useQuery({
    queryKey: deadlinesKeys.list(query),
    queryFn: () => api.get<Paginated<Deadline>>('/deadlines', { query }),
    placeholderData: (previous) => previous,
  })
}

/** Widget pulpitu „Najbliższe terminy" — `GET /api/deadlines/upcoming` bez parametrów. */
export function useUpcomingDeadlines(size = 5) {
  return useQuery({
    queryKey: [...deadlinesKeys.upcoming, size],
    queryFn: () => api.get<Paginated<Deadline>>('/deadlines/upcoming', { query: { size } }),
    staleTime: 60_000,
  })
}

export function useDeadlineStats() {
  return useQuery({
    queryKey: deadlinesKeys.stats,
    queryFn: () => api.get<DeadlineStats>('/deadlines/stats'),
    staleTime: 60_000,
  })
}

/** Prawnicy do wyboru właściciela — endpoint modułu `cases` (zawsze włączony). */
export function useLawyers(enabled = true) {
  return useQuery({
    queryKey: deadlinesKeys.lawyers,
    queryFn: () => api.get<LawyerOption[]>('/cases/lawyers'),
    staleTime: 5 * 60_000,
    enabled,
  })
}

/** Sprawy do wyboru w formularzu nowego terminu. */
export function useCaseOptions(enabled = true) {
  return useQuery({
    queryKey: deadlinesKeys.cases,
    queryFn: () => api.get<Paginated<CaseOption>>('/cases', { query: { size: 200 } }),
    staleTime: 5 * 60_000,
    enabled,
  })
}

export function useCreateDeadline() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: DeadlineCreatePayload) => api.post<Deadline>('/deadlines', payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: deadlinesKeys.all })
    },
  })
}

export function useUpdateDeadline() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...payload }: DeadlineUpdatePayload & { id: number }) =>
      api.patch<Deadline>(`/deadlines/${id}`, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: deadlinesKeys.all })
    },
  })
}

export function useCompleteDeadline() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post<Deadline>(`/deadlines/${id}/complete`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: deadlinesKeys.all })
    },
  })
}
